import { palette } from '@openx/components-core/lib/theme/paletteV2';
import type { DealInsightsBidRateWinRateApi } from '@openx/types/dealInsights';
import { toPercentString } from '@openx/utils/lib/toPercentString';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { buildLabel, buildTicks, XAxisTick, YAxisTick } from '../../chartUtils';

type RateComparisonChartCoreProps = {
  values: DealInsightsBidRateWinRateApi[];
  isLoading?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  tooltipContent?: ReactNode;
  isAnimationActive?: boolean;
};

export const RateComparisonChartCore = ({
  values,
  isLoading = false,
  showLegend = false,
  showTooltip = false,
  tooltipContent,
  isAnimationActive = true,
}: RateComparisonChartCoreProps) => {
  const { t } = useTranslation();

  const bidRateMax = values.reduce((max, entry) => Math.max(max, entry?.bid_rate ?? 0), 0);
  const winRateMax = values.reduce((max, entry) => Math.max(max, entry?.win_rate ?? 0), 0);
  const bidRateScale = buildTicks(bidRateMax);
  const winRateScale = buildTicks(winRateMax);

  return (
    <ResponsiveContainer>
      <ComposedChart data={values} style={{ overflow: 'visible' }} margin={{ top: 28, right: 0, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke={palette.divider} />
        {showLegend && <Legend verticalAlign="top" height={36} />}
        <XAxis
          dataKey="date"
          stroke={palette.divider}
          height={60}
          tick={props => <XAxisTick {...props} isLoading={isLoading} />}
          {...(isLoading && { tickCount: 5, interval: 0 })}
        />
        <YAxis
          yAxisId="left"
          orientation="left"
          stroke="transparent"
          label={{ ...buildLabel(t('Bid Rate')), fill: isLoading ? palette.divider : palette.text.primary }}
          domain={bidRateScale.domain}
          ticks={bidRateScale.ticks}
          interval={0}
          tick={props => (
            <YAxisTick
              {...props}
              isLoading={isLoading}
              isLeftAxis={true}
              leftScale={bidRateScale}
              rightScale={winRateScale}
              formatter={val => toPercentString(val, { decimalPlaces: 2 }) ?? ''}
            />
          )}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          stroke="transparent"
          label={{ ...buildLabel(t('Win Rate')), fill: isLoading ? palette.divider : palette.text.primary }}
          domain={winRateScale.domain}
          ticks={winRateScale.ticks}
          interval={0}
          tick={props => (
            <YAxisTick
              {...props}
              isLoading={isLoading}
              isLeftAxis={false}
              leftScale={bidRateScale}
              rightScale={winRateScale}
              formatter={val => toPercentString(val) ?? ''}
            />
          )}
        />
        {showTooltip && tooltipContent}
        <Bar
          yAxisId="left"
          dataKey="bid_rate"
          name={t('Bid Rate')}
          fill={isLoading ? palette.divider : palette.charts.lightBlue}
          barSize={125}
          style={{ opacity: isLoading ? 0.5 : 1 }}
          isAnimationActive={isAnimationActive}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="win_rate"
          name={t('Win Rate')}
          stroke={isLoading ? palette.divider : palette.charts.blue}
          strokeWidth={2}
          dot={{ r: 4, fill: isLoading ? palette.divider : palette.charts.blue }}
          activeDot={{
            r: 5,
            fill: palette.common.white,
            stroke: isLoading ? palette.divider : palette.charts.blue,
            strokeWidth: 2,
          }}
          isAnimationActive={isAnimationActive}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
